"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Languages } from "lucide-react";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useTransition } from "react";
import { locales, getLocaleConfig } from "@/i18n/locales";
import Image from "next/image";
import { updateUserLocale } from "@/app/[locale]/actions/locale";

// Create a component for the flag icon
const FlagIcon = ({ countryCode, className = "" }: { countryCode: string; className?: string }) => {
  return (
    <Image
      src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode}.svg`}
      alt={`${countryCode} flag`}
      width={16}
      height={12}
      className={`inline-block rounded-sm ${className}`}
      style={{ width: '16px', height: '12px' }}
    />
  );
};

const LanguageSwitcher = ({ className = "" }: { className?: string }) => {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = useLocale();

  const currentLocaleConfig = getLocaleConfig(currentLocale);

  const handleLocaleChange = (newLocale: string) => {
    if (newLocale === currentLocale) return;

    startTransition(async () => {
      try {
        // First update the user's preferred locale in the database
        await updateUserLocale(newLocale);

        // Then navigate using next-intl's navigation
        router.replace(pathname, { locale: newLocale });
      } catch (error) {
        console.error("Error updating locale:", error);
        // Fallback to just navigation if server action fails
        router.replace(pathname, { locale: newLocale });
      }
    });
  };

  const ICON_SIZE = 16;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" disabled={isPending}>
          <div className="flex items-center gap-2">
            {currentLocaleConfig?.countryCode ? (
              <FlagIcon countryCode={currentLocaleConfig.countryCode} />
            ) : (
              <Languages size={ICON_SIZE} className="text-muted-foreground" />
            )}
            <span className="hidden sm:inline text-xs">
              {currentLocaleConfig?.id.toUpperCase()}
            </span>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={`w-48 overflow-y-auto ${className}`} align="start">
        <DropdownMenuRadioGroup
          value={currentLocale}
          onValueChange={handleLocaleChange}
        >
          {locales.map((locale) => (
            <DropdownMenuRadioItem
              key={locale.id}
              className="flex items-center gap-3 cursor-pointer"
              value={locale.id}
            >
              {locale.countryCode ? (
                <FlagIcon countryCode={locale.countryCode} />
              ) : (
                <div className="w-4 h-3" /> // placeholder to maintain alignment
              )}
              <div className="flex flex-col">
                <span className="font-medium">{locale.nativeName}</span>
                <span className="text-xs text-muted-foreground">
                  {locale.name}
                </span>
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { LanguageSwitcher };
